import React, { useEffect, useState, useCallback } from 'react';
import '../styles/MessageBox.css';

const MessageBox = ({ message, type, onClose, duration = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => onClose && onClose(), 300);
  }, [onClose]);

  useEffect(() => {
    // Show animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto close if duration is set
    if (duration > 0) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev - (100 / (duration / 50));
          if (newProgress <= 0) {
            handleClose();
            return 0;
          }
          return newProgress;
        });
      }, 50);

      return () => clearInterval(interval);
    }
  }, [duration, handleClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <div className="message-icon success-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 12.5L10.5 15L16 9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="message-icon error-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 7V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="16.5" r="1" fill="currentColor"/>
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="message-icon warning-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 20h20L12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1" fill="currentColor"/>
            </svg>
          </div>
        );
      case 'loading':
        return (
          <div className="message-icon loading-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.2"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 12 12"
                  to="360 12 12"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
          </div>
        );
      case 'info':
        return (
          <div className="message-icon info-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="8" r="1" fill="currentColor"/>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'success':
        return 'موفقیت‌آمیز';
      case 'error':
        return 'خطا';
      case 'warning':
        return 'هشدار';
      case 'loading':
        return 'در حال پردازش...';
      case 'info':
        return 'اطلاعات';
      default:
        return '';
    }
  };

  return (
    <>
      <div className={`message-overlay ${isVisible ? 'visible' : ''}`} onClick={type !== 'loading' ? handleClose : undefined} />
      <div className={`message-box ${type} ${isVisible ? 'visible' : ''}`}>
        <div className="message-box-content">
          {getIcon()}
          <div className="message-box-text">
            <h3 className="message-title">{getTitle()}</h3>
            <div className="message-description" dangerouslySetInnerHTML={{ __html: message }} />
          </div>
          {type !== 'loading' && (
            <button className="message-close-btn-small" onClick={handleClose} aria-label="بستن" data-testid="message-close-small-btn">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
        {type !== 'loading' && (
          <button className="message-understood-btn" onClick={handleClose} data-testid="message-understood-btn">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            متوجه شدم
          </button>
        )}
        {duration > 0 && type !== 'loading' && (
          <div className="message-progress-bar">
            <div className="message-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </>
  );
};

export default MessageBox;